import { FC, useContext, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  H2,
  IDropdownItem,
  Modal,
  Preloader,
  Search,
  Separator,
  Tabs,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';

import { ETabsModal } from '../interfaces';
import { GET_GOODS, GET_SERVICES, GET_TARIFFS } from '../schema';
import s from './AddPositionModal.module.scss';
import { IAddPositionModalProps } from './interfaces';
import { getSearchPlaceholder } from './utils';

export const AddPositionModal: FC<IAddPositionModalProps> = ({
  onClose,
  chosenPositions,
  setChosenPositions,
}) => {
  const { t } = useContext(LocalizationContext);
  const [tab, setTab] = useState<ETabsModal>(ETabsModal.GOODS);
  const [closeDropDown, setCloseDropDown] = useState<boolean>(false);
  const [tempPositions, setTempPositions] = useState(chosenPositions);

  const { data: goodsData, loading: goodsLoading } = useQuery(GET_GOODS, {
    variables: {
      goodsInput2: { use_global_discounts: true },
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: servicesData, loading: servicesLoading } = useQuery(
    GET_SERVICES,
    {
      variables: {
        input: { use_global_discounts: true },
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: tariffsData, loading: tariffsLoading } = useQuery(GET_TARIFFS, {
    variables: {
      filter: { use_global_discounts: true },
    },
    fetchPolicy: 'cache-and-network',
  });

  const isLoading = goodsLoading || servicesLoading || tariffsLoading;

  const handleTabChange = (value: ETabsModal): void => {
    setTab(value);
  };

  const getAllTitle = (tab: ETabsModal): string => {
    switch (tab) {
      case ETabsModal.GOODS:
        return t('promo.position.all_goods');
      case ETabsModal.SERVICES:
        return t('promo.position.all_services');
      case ETabsModal.TARIFFS:
        return t('promo.position.all_tariffs');
      default:
        return t('visitors.types.all');
    }
  };

  const OPTIONS = useMemo<IDropdownItem<number, unknown>[]>(() => {
    const hasSpecificItems = tempPositions.some(
      (p) => p.type === tab && p.id !== -1
    );

    const baseOptions: IDropdownItem<number, unknown>[] = [];

    if (!hasSpecificItems) {
      baseOptions.push({
        value: -1,
        title: getAllTitle(tab),
      });
    }

    switch (tab) {
      case ETabsModal.GOODS:
        return [
          ...baseOptions,
          ...(goodsData?.goods
            ?.filter(
              (good: { title: string; id: number }) =>
                !tempPositions.find(
                  (item) =>
                    item.id === good?.id && item.type === ETabsModal.GOODS
                )
            )
            ?.map((good: { title: string; id: number }) => ({
              value: Number(good.id),
              title: good.title,
            })) || []),
        ];

      case ETabsModal.SERVICES:
        return [
          ...baseOptions,
          ...(servicesData?.services
            ?.filter(
              (service: { title: string; id: number }) =>
                !tempPositions.find(
                  (item) =>
                    item.id === service?.id && item.type === ETabsModal.SERVICES
                )
            )
            ?.map((service: { title: string; id: number }) => ({
              value: Number(service.id),
              title: service.title,
            })) || []),
        ];

      case ETabsModal.TARIFFS:
        return [
          ...baseOptions,
          ...(tariffsData?.tariffs?.data
            ?.filter(
              (tariff: { title: string; id: number }) =>
                !tempPositions.find(
                  (item) =>
                    item.id === tariff?.id && item.type === ETabsModal.TARIFFS
                )
            )
            ?.map((tariff: { title: string; id: number }) => ({
              value: Number(tariff.id),
              title: tariff.title,
            })) || []),
        ];

      default:
        return baseOptions;
    }
  }, [tab, goodsData, servicesData, tariffsData, tempPositions, t]);

  const handleAddPosition = (value: number | null): void => {
    if (value === null) return;

    if (value === -1) {
      setTempPositions((prev) => [
        ...prev.filter((p) => p.type !== tab),
        { id: -1, title: getAllTitle(tab), type: tab },
      ]);
      return;
    }
    setCloseDropDown(true);

    setTempPositions((prev) => {
      const withoutAll = prev.filter((p) => !(p.type === tab && p.id === -1));

      let foundItem: { title: string; id: number } | undefined;
      switch (tab) {
        case ETabsModal.GOODS:
          foundItem = goodsData?.goods?.find(
            (item: { title: string; id: number }) => item.id === value
          );
          break;
        case ETabsModal.SERVICES:
          foundItem = servicesData?.services?.find(
            (item: { title: string; id: number }) => item.id === value
          );
          break;
        case ETabsModal.TARIFFS:
          foundItem = tariffsData?.tariffs?.data?.find(
            (item: { title: string; id: number }) => item.id === value
          );
          break;
      }

      if (foundItem) {
        return [...withoutAll, { ...foundItem, type: tab }];
      }
      return withoutAll;
    });
  };

  const handleRemovePosition = (item: {
    title: string;
    id: number;
    type?: ETabsModal;
  }): void => {
    setTempPositions((prev) =>
      prev.filter(
        (position) => !(position.id === item.id && position.type === item.type)
      )
    );
  };

  const TABS_OPTIONS = [
    { title: t('category.goods'), value: ETabsModal.GOODS },
    { title: t('category.services'), value: ETabsModal.SERVICES },
    { title: t('category.tariffs'), value: ETabsModal.TARIFFS },
  ];

  const handleSave = (): void => {
    setChosenPositions(tempPositions);
    onClose();
  };

  const handleCancel = (): void => {
    onClose();
  };

  return (
    <Modal className={s.container} onClose={handleCancel}>
      <div className={s.header}>
        <H2>{t('promo.position.modal.title')}</H2>
        <span className={s.text}>{t('promo.position.modal.description')}</span>
      </div>

      <Preloader loading={isLoading}>
        <div className={s.tabs_block}>
          <Tabs
            className={s.tabs}
            options={TABS_OPTIONS}
            value={tab}
            onChange={handleTabChange}
          />
          <div>
            <Search
              closeDropDown={closeDropDown}
              name={'select'}
              items={OPTIONS}
              value={null}
              onChange={(value) => {
                handleAddPosition(value);
                setCloseDropDown(true);

                setTimeout(() => setCloseDropDown(false), 0);
              }}
              placeholder={getSearchPlaceholder(t, tab)}
              variant="secondary"
              className={cn(
                s.search,
                tempPositions.some((p) => p.type === tab && p.id === -1) &&
                  s.disabled
              )}
              onInputChange={() => {
                setCloseDropDown(false);
              }}
              notFoundText={t('promo.add.modal.search')}
              autocomplete="off"
              preventSubmitOnEnter={true}
              disabled={tempPositions.some(
                (p) => p.type === tab && p.id === -1
              )}
            />
          </div>
        </div>

        <Separator />

        <div className={cn(s.chosen, tempPositions.length && s.active)}>
          {tempPositions.length ? (
            <>
              <span className={s.text}>{t('promo.position.chosen')}</span>
              <div className={s.items}>
                {tempPositions.map((item) => (
                  <div key={`${item.type}-${item.id}`} className={s.item}>
                    <span className={s.item_text}>{item.title}</span>
                    <Button
                      onClick={() => handleRemovePosition(item)}
                      iconName="CloseIcon"
                      onlyIcon
                      className={s.button}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <span className={s.text}>{t('promo.position.modal.chosen')}</span>
          )}
        </div>

        <div className={s.buttons_block}>
          <Button
            onClick={handleSave}
            fullWidth
            disabled={!tempPositions.length}
          >
            {t('add')}
          </Button>
          <Button fullWidth variant="secondary" onClick={handleCancel}>
            {t('cancel')}
          </Button>
        </div>
      </Preloader>
    </Modal>
  );
};
